import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch } from "wouter";
import CookieBanner from "./components/cookie/CookieBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import PortalGate from "./components/PortalGate";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Plan from "./pages/Plan";
import CustomerPortal from "./pages/CustomerPortal";
import RemoteSiteSurvey from "./pages/RemoteSiteSurvey";
import InstallerPortal from "./pages/InstallerPortal";
import InstallerEnaDnoReview from "./pages/InstallerEnaDnoReview";
import InstallerSurveyList from "./pages/InstallerSurveyList";
import AdminDashboardHop from "./pages/AdminDashboardHop";
import SpaHandoff from "./pages/SpaHandoff";
import SelectPortal from "./pages/SelectPortal";
import InstallerApply from "./pages/InstallerApply";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import Cookies from "./pages/legal/Cookies";
import About from "./pages/About";
import Faq from "./pages/Faq";
import Contact from "./pages/Contact";
import Methodology from "./pages/Methodology";
import SharedReport from "./pages/SharedReport";
import EnergyPlan from "@/pages/EnergyPlan";
import Pay from "@/pages/Pay";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import Network from "@/pages/Network";
import Score from "@/pages/Score";

function QuoteRedirect() {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return <Redirect to={`/plan${search}`} />;
}

function FinanceRedirect() {
  return <Redirect to="/" />;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/quote" component={QuoteRedirect} />
      <Route path="/select-portal" component={SelectPortal} />
      <Route path="/portal/survey/:projectId">
        {() => (
          <PortalGate portal="customer">
            <RemoteSiteSurvey />
          </PortalGate>
        )}
      </Route>
      <Route path="/portal">
        {() => (
          <PortalGate portal="customer">
            <CustomerPortal />
          </PortalGate>
        )}
      </Route>
      <Route path="/installer/apply" component={InstallerApply} />
      <Route path="/installer/surveys/:id">
        {() => (
          <PortalGate portal="installer">
            <RemoteSiteSurvey />
          </PortalGate>
        )}
      </Route>
      <Route path="/installer/surveys">
        {() => (
          <PortalGate portal="installer">
            <InstallerSurveyList />
          </PortalGate>
        )}
      </Route>
      <Route path="/installer/dno/:quoteId/review">
        {() => (
          <PortalGate portal="installer">
            <InstallerEnaDnoReview />
          </PortalGate>
        )}
      </Route>
      <Route path="/installer">
        {() => (
          <PortalGate portal="installer">
            <InstallerPortal />
          </PortalGate>
        )}
      </Route>
      <Route path="/admin">
        {() => (
          <PortalGate portal="admin">
            <AdminDashboardHop />
          </PortalGate>
        )}
      </Route>
      <Route path="/about" component={About} />
      <Route path="/faq" component={Faq} />
      <Route path="/contact" component={Contact} />
      <Route path="/methodology" component={Methodology} />
      <Route path="/score" component={Score} />
      <Route path="/finance" component={FinanceRedirect} />
      <Route path="/network" component={Network} />
      <Route path="/plan" component={Plan} />
      <Route path="/blog/:slug" component={BlogPost} />
      <Route path="/blog" component={BlogIndex} />
      <Route path="/r/:token" component={SharedReport} />
      <Route path="/plan/:propertyId" component={EnergyPlan} />
      <Route path="/pay/:transactionId" component={Pay} />
      <Route path="/pay" component={Pay} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/cookies" component={Cookies} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="dark"
        // switchable
      >
        <TooltipProvider>
          <Toaster richColors position="top-right" />
          <SpaHandoff>
            <Router />
          </SpaHandoff>
          <CookieBanner />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
